import { useEffect, useState } from "react";
import classNames from "classnames";
import { useMantineTheme } from "@mantine/core";
import Image from "next/image";
import { DepartmentDetails } from "@/interfaces/departmentDetails";
import { Section } from "@/components/common/section/Section";
import {
  addBasePathToStrapiMedia,
  formatStrapiEntity,
  StrapiEntity,
  StrapiImage,
} from "@/utils/strapi";
import HydratedMediaQuery from "@/utils/hydratedMediaQuery";
import { getBreakpoint } from "@/utils/mantine";
import styles from "./DepartmentBlock.module.scss";
import { DepartmentLink } from "./departmentLink/DepartmentLink";

export type DepartmentBlockContent = {
  title: string | undefined;
  desktopBackgroundImage: StrapiEntity<StrapiImage> | undefined;
  mobileBackgroundImage: StrapiEntity<StrapiImage> | undefined;
};

export type DepartmentBlockProps = DepartmentBlockContent & {
  departments: DepartmentDetails[];
};

const letterFilter = [
  ["A"],
  ["B", "C"],
  ["D", "E"],
  ["F", "G"],
  ["H"],
  ["I", "J"],
  ["L"],
  ["M", "N"],
  ["O", "P", "R"],
  ["S", "T"],
  ["V", "Y"],
];

export const DepartmentBlock = ({
  title,
  desktopBackgroundImage,
  mobileBackgroundImage,
  departments,
}: DepartmentBlockProps) => {
  const theme = useMantineTheme();

  const [selectedLetterFilter, setSelectedLetterFilter] = useState<string[]>(
    letterFilter[0]
  );
  const [displayedDepartments, setDisplayedDepartments] = useState<
    DepartmentDetails[]
  >([]);

  const orderedDepartments = departments.sort((prev, next) => {
    if (prev.name > next.name) {
      return 1;
    } else if (prev.name < next.name) {
      return -1;
    } else {
      return 0;
    }
  });

  useEffect(() => {
    const departmentsToDisplay = orderedDepartments.filter((department) => {
      return selectedLetterFilter.some((range) =>
        department.name.toUpperCase().startsWith(range.toUpperCase())
      );
    });
    setDisplayedDepartments(departmentsToDisplay);
  }, [selectedLetterFilter, orderedDepartments]);

  const isDisplayed = (department: DepartmentDetails) => {
    return displayedDepartments.some(
      (displayedDepartment) => displayedDepartment.slug === department.slug
    );
  };
  const isActive = (filter: string[]) => {
    return (
      JSON.stringify(filter).toUpperCase() ===
      JSON.stringify(selectedLetterFilter).toUpperCase()
    );
  };

  const isTabletOrDesktop = HydratedMediaQuery(
    getBreakpoint(theme.breakpoints.sm)
  );

  let backgroundImage: StrapiImage | null = null;

  if (isTabletOrDesktop) {
    backgroundImage =
      desktopBackgroundImage?.data != null
        ? formatStrapiEntity(desktopBackgroundImage)
        : null;
  } else {
    backgroundImage =
      mobileBackgroundImage?.data != null
        ? formatStrapiEntity(mobileBackgroundImage)
        : null;
  }

  return (
    <div className={styles.departmentBlock}>
      {backgroundImage != null && (
        <div className={styles.backgroundImageContainer}>
          <Image
            src={addBasePathToStrapiMedia(backgroundImage)}
            objectFit="contain"
            objectPosition="center bottom"
            alt={backgroundImage.alternativeText}
            layout="fill"
          />
        </div>
      )}
      <Section containerStyle={styles.departmentSection}>
        {title !== undefined && <h2 className={styles.title}>{title}</h2>}
        <div className={styles.letterFilters}>
          {letterFilter.map((filter, index) => {
            const displayedFilter = filter.join(" - ");

            return (
              <div
                key={index}
                onClick={() => setSelectedLetterFilter(letterFilter[index])}
                className={classNames(styles.letterFilter, {
                  [styles.isActive]: isActive(filter),
                })}
              >
                {displayedFilter}
              </div>
            );
          })}
        </div>
        <div className={styles.filteredDepartment}>
          <div className={styles.filteredDepartmentGrid}>
            {orderedDepartments.map((department) => {
              return (
                <div
                  key={department.name}
                  className={classNames(styles.department, {
                    [styles.notDisplayed]: !isDisplayed(department),
                  })}
                >
                  <DepartmentLink {...department}></DepartmentLink>
                </div>
              );
            })}
          </div>
        </div>
      </Section>
    </div>
  );
};
